// Ratio display scale — two user-selectable faces: "raw" and "Normalized".
//
//   RAW (default): the ratio as-is (ask/bid, SPEC §2). 2.0 reads 2.0, below-1
//     reads as a decimal. Balance = 1.0.
//   NORMALIZED (Owner "Option B", 2026-08-21): at or above 1 the raw ratio,
//     unchanged and UNCAPPED (15.05 reads 15.05); below 1 the reciprocal
//     mirror, -1 / ratio, also uncapped (2:1 bids = -2.00, 3:1 = -3.00,
//     always, by arithmetic). Replaces the rarity-matched Option A table
//     ("we never should have weighted rarity"). Numbers are never capped;
//     only chart PIXELS pin at PLOT_FLOOR (see plotFloor()).
//
// The mode is chosen at runtime by the on-page toggle (persisted in
// localStorage) and can also be forced on by data-log-scale="true" on #app
// (the legacy zero.html copy). RAW is the default, so an untouched dashboard
// behaves exactly as before.

const STORAGE_KEY = "bitclock-scale";

function readInitialMode(): boolean {
  try {
    // zero.html forces Normalized via the data attribute; otherwise honor the
    // persisted choice; default to raw.
    if (
      typeof document !== "undefined" &&
      typeof document.getElementById === "function" &&
      document.getElementById("app")?.getAttribute("data-log-scale") === "true"
    ) {
      return true;
    }
    return typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY) === "normalized";
  } catch {
    return false;
  }
}

let _normalized = readInitialMode();

/** True when the Normalized scale is active. */
export function isNormalized(): boolean {
  return _normalized;
}

/** Switch scale and persist the choice. Callers must re-render afterward. */
export function setNormalized(on: boolean): void {
  _normalized = on;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, on ? "normalized" : "raw");
  } catch {
    /* private-mode / storage-disabled: mode still applies for this session */
  }
}

// Chart-geometry pin (Owner, 2026-08-21): bars/axis/markers never DRAW below
// this on the Normalized face — deep readings pin to the bottom edge so one
// -37 bar (or a feed-artifact monster) can't flatten the chart. NUMBERS are
// never pinned: the big numeral, R-LOD, info stats and tooltips show the
// exact value. Real sub- -10 readings are rare (17 days in ~315, all
// legacy-era; the other 2 of the old 19 were artifacts — 2026-08-13
// rebuild, 2026-05-08 outage. Live prod floor 2026-08-28: sampled
// 0.20198, displayed/window 0.1832 — analyst fact-check, hq c01b6cd).
const PLOT_FLOOR = -10;

/**
 * Map a raw ratio to the Normalized signed level (Option B, reciprocal).
 * At or above 1: the raw ratio, uncapped. Below 1: -1 / ratio, uncapped
 * (true mirror: 0.5 -> -2, 0.333.. -> -3). Nonpositive input has no finite
 * mirror; -Infinity keeps formatters ("--.--") and the plot pin honest.
 * Only meaningful when the Normalized face is active.
 */
export function ratioToScore(ratio: number): number {
  if (!(ratio > 0)) return Number.NEGATIVE_INFINITY;
  return ratio >= 1 ? ratio : -1 / ratio;
}

/**
 * Inverse of ratioToScore: map a Normalized signed level back to the raw
 * ratio. Scores never land in the open interval (-1, 1) — that dead zone,
 * and any non-finite input, returns null so callers can reject it. Both
 * 1 and -1 map to ratio 1.0 (the balance point).
 */
export function scoreToRatio(score: number): number | null {
  if (!Number.isFinite(score) || Math.abs(score) < 1) return null;
  return score >= 1 ? score : -1 / score;
}

/** Lower bound for chart GEOMETRY only: -10 when Normalized, unbounded raw. */
export function plotFloor(): number {
  return _normalized ? PLOT_FLOOR : Number.NEGATIVE_INFINITY;
}

/** Chart baseline in plot space: 0 (up/down divider) when Normalized, else 1.0 raw. */
export function center(): number {
  return _normalized ? 0 : 1.0;
}

/** Map a raw ratio into plot space (signed level when Normalized, else unchanged). */
export function toPlot(ratio: number): number {
  return _normalized ? ratioToScore(ratio) : ratio;
}

/**
 * Format a raw RATIO for the big number / R-HOD / R-LOD. Raw face is identical
 * to util/format.ts's formatRatio (null -> "--.--", else toFixed(2)); the
 * Normalized face shows the signed level to 2 decimals (no leading "+").
 */
export function formatDisplayRatio(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined) return "--.--";
  if (!_normalized) return ratio.toFixed(2);
  const score = ratioToScore(ratio);
  return Number.isFinite(score) ? score.toFixed(2) : "--.--";
}

/** Format an already-in-plot-space value (chart Y axis / info / tooltip). */
export function formatPlotValue(v: number): string {
  return Number.isFinite(v) ? v.toFixed(2) : "--.--";
}
